/**
 * The page the browser lands on after a Teslyn Field sign-in (`/field/signed-in`; MB request R19, 24 Oct 2026).
 * Supabase sends the browser here with the PKCE `?code=` (or `#error=`); the page hands it to Teslyn Field through
 * the deep link `<scheme>://auth/callback` and says the window can be closed, instead of leaving a blank tab. The
 * code is only useful to the laptop that asked (its code verifier is in the credential store), so this page never
 * exchanges it. This address must be in the Supabase project's redirect list.
 *
 * Each Teslyn Field channel registers its own scheme (`teslyn-dev`, `teslyn-beta`, `teslyn`; 26 Oct 2026), so a
 * PC with two of them never opens the wrong one: Teslyn Field names its scheme in the return address
 * (`?app=teslyn-beta`), checked against the list below, and the page opens that one. With no name, or one not in the
 * list, the page opens `teslyn://`, which builds before 2026.10.10 register. The name is dropped from what is handed
 * on; everything else Supabase sent travels unchanged.
 *
 * Since 3 Nov 2026 the email's link comes here first, with the token hash instead of the code (`?token_hash=…&type=…`;
 * the Send Email hook, packages/domain auth-email.ts `authLinkUrl`), because Supabase spends the token on its own
 * link's GET and a mail scanner opening the email's links spent it. This page spends nothing on load: "Sign in to
 * Teslyn Field" opens Supabase's link (`/auth/v1/verify?token=…&redirect_to=<this page>`), which spends the token and
 * sends the browser back here with the `?code=` Teslyn Field exchanges with its code verifier, as before. Teslyn
 * Field itself is unchanged.
 */
import { useEffect, useState, type ReactElement } from 'react';
import type { EmailOtpType } from '@supabase/supabase-js';
import { authVerifyUrl, type AuthEmailAction } from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { PoweredBy } from '../components/States.tsx';
import { readConfig } from '../config.ts';
import { LegalLinks } from '../legal/LegalPage.tsx';

export const FIELD_RETURN_PATH = '/field/signed-in';
/** The query parameter Teslyn Field names its scheme in. */
export const FIELD_APP_PARAM = 'app';

/** The schemes a Teslyn Field registers, and what to call each: the only ones this page will ever open. */
export const FIELD_APPS = {
  teslyn: 'Teslyn Field',
  'teslyn-beta': 'Teslyn Field (beta)',
  'teslyn-dev': 'Teslyn Field (dev)',
} as const;
export type FieldScheme = keyof typeof FIELD_APPS;
const DEFAULT_SCHEME: FieldScheme = 'teslyn';

/** Whether this address is the return page (the only route of the portal that needs no tenant or session). */
export function isFieldReturnPage(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === FIELD_RETURN_PATH;
}

/** The query as `key=value` parts, without the leading `?` (empty for no query). */
function queryParts(search: string): string[] {
  const q = search.replace(/^\?/, '');
  return q ? q.split('&') : [];
}

/** The scheme to open: the one Teslyn Field named, when it is a known one; `teslyn` otherwise. */
export function fieldScheme(search: string): FieldScheme {
  const named = new URLSearchParams(search).get(FIELD_APP_PARAM) ?? '';
  return Object.prototype.hasOwnProperty.call(FIELD_APPS, named) ? (named as FieldScheme) : DEFAULT_SCHEME;
}

/**
 * The deep link that carries what Supabase sent (`?code=…`, or `#error=…&error_description=…`) to Teslyn Field, on
 * the scheme Teslyn Field named. Only the `app` part is dropped; the rest of the query and the fragment are
 * handed on as they came.
 */
export function fieldReturnLink(location: { readonly search: string; readonly hash: string }): string {
  const rest = queryParts(location.search).filter((part) => part.split('=')[0] !== FIELD_APP_PARAM);
  const search = rest.length ? `?${rest.join('&')}` : '';
  return `${fieldScheme(location.search)}://auth/callback${search}${location.hash}`;
}

/** The refusal in Supabase's answer, in words, or null when the sign-in went through. */
export function fieldReturnError(location: {
  readonly search: string;
  readonly hash: string;
}): string | null {
  const params = new URLSearchParams(location.hash.replace(/^#/, ''));
  for (const [k, v] of new URLSearchParams(location.search)) params.set(k, v);
  const desc = params.get('error_description') ?? params.get('error');
  return desc ? desc.replace(/\+/g, ' ') : null;
}

const LINK_TYPES: readonly string[] = ['invite', 'magiclink', 'email', 'signup', 'recovery', 'email_change'];

/** The email link's token hash and type, when the address carries them (and no code yet); null otherwise. */
export function fieldLinkParts(
  search: string,
): { readonly tokenHash: string; readonly type: EmailOtpType } | null {
  const q = new URLSearchParams(search);
  const tokenHash = q.get('token_hash') ?? '';
  const type = q.get('type') ?? '';
  if (q.has('code') || !tokenHash || !LINK_TYPES.includes(type)) return null;
  return { tokenHash, type: type as EmailOtpType };
}

/**
 * Supabase's link that spends the token hash and sends the browser back to this page with the code: the return
 * address is this page with everything but the hash and type (`app=`, `sb_flow_id=`) kept, so the code reaches the
 * Teslyn Field that asked. The return address must be on the project's redirect list (it is what Teslyn Field asked
 * with).
 */
export function fieldOpenLink(
  supabaseUrl: string,
  origin: string,
  location: { readonly search: string },
): string | null {
  const parts = fieldLinkParts(location.search);
  if (!parts) return null;
  const back = new URL(FIELD_RETURN_PATH, origin);
  for (const [k, v] of new URLSearchParams(location.search))
    if (k !== 'token_hash' && k !== 'type') back.searchParams.append(k, v);
  return authVerifyUrl(supabaseUrl, parts.tokenHash, parts.type as AuthEmailAction, back.toString());
}

export function FieldSignedIn({
  location = window.location,
  open = (url) => window.location.replace(url),
  origin = window.location.origin,
  supabaseUrl = readConfig().supabaseUrl,
}: {
  readonly location?: { readonly search: string; readonly hash: string };
  /** Opens the deep link (the browser asks to open Teslyn Field), or Supabase's link for an email link. */
  readonly open?: (url: string) => void;
  readonly origin?: string;
  readonly supabaseUrl?: string;
}): ReactElement {
  const app = FIELD_APPS[fieldScheme(location.search)];
  const viaEmail = fieldOpenLink(supabaseUrl, origin, location);
  if (viaEmail) return <FieldLinkConfirm app={app} open={() => open(viaEmail)} />;
  return <FieldReturn location={location} open={open} app={app} />;
}

/** The email link's screen: nothing is spent until the button is pressed. */
function FieldLinkConfirm({ app, open }: { readonly app: string; readonly open: () => void }): ReactElement {
  const [opened, setOpened] = useState(false);
  return (
    <div className="signin">
      <main className="signin__card" aria-labelledby="field-link-title">
        <h1 id="field-link-title" className="title">
          Sign in to {app}
        </h1>
        <p className="body">
          You opened a sign-in link from a Teslyn email. Press the button on the computer where {app} asked
          for it, and allow the browser to open {app}. Nothing happens until you press it; if you did not ask
          for this email, close this page and ignore the email.
        </p>
        <Button
          variant="primary"
          onClick={() => {
            setOpened(true);
            open();
          }}
        >
          Sign in to {app}
        </Button>
        {opened && (
          <p className="caption muted" role="status">
            Opening {app}…
          </p>
        )}
      </main>
      <footer className="signin__foot">
        <PoweredBy />
        <LegalLinks />
      </footer>
    </div>
  );
}

function FieldReturn({
  location,
  open,
  app,
}: {
  readonly location: { readonly search: string; readonly hash: string };
  readonly open: (url: string) => void;
  readonly app: string;
}): ReactElement {
  const link = fieldReturnLink(location);
  const refused = fieldReturnError(location);
  const [opened, setOpened] = useState(false);
  useEffect(() => {
    open(link);
    setOpened(true);
    // a tab the browser opened for the sign-in may be closed by script; one the person opened may not, so the words
    // below stay
    const t = setTimeout(() => {
      try {
        window.close();
      } catch {
        /* the browser keeps the tab: the person closes it */
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [link, open]);
  return (
    <div className="signin">
      <main className="signin__card" aria-labelledby="field-return-title">
        <h1 id="field-return-title" className="title">
          {refused ? 'Sign-in refused' : `Signed in to ${app}`}
        </h1>
        {refused ? (
          <p className="body" role="alert">
            {refused}. {app} shows this too; try again from there.
          </p>
        ) : (
          <p className="body" role="status">
            {opened ? `${app} is finishing your sign-in.` : `Opening ${app}…`} You can close this window.
          </p>
        )}
        <p className="caption muted">
          If {app} did not open, press the button below and allow the browser to open it.
        </p>
        <Button variant="primary" onClick={() => open(link)}>
          Open {app}
        </Button>
      </main>
      <footer className="signin__foot">
        <PoweredBy />
        <LegalLinks />
      </footer>
    </div>
  );
}
